import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { formatUnits } from "@/helper/viem";

import { REBIRTH_OPTIONS } from "@/constants/rebirth";
import { getTokenBalance } from "@/helper/contractFunctions";
import {
  getRebirthOption,
  initiateRebirth,
  quoteRebirth,
} from "@/helper/contractFunctions/MafiaRebirth";
import { getSwapTokens } from "@/helper/contractFunctions/MafiaSwapRouter";
import { SwapToken } from "@/types/Contract/SwapToken";
import config from "@/config/config";

export const REBIRTH_PAYMENT_BUFFER = 1.05;

export interface RebirthOptionQuote {
  usdCost: number;
  enabled: boolean;
}

export interface RebirthState {
  isRebirthing: boolean;
  selectedOptionId: number | null;
  swapTokenInfo: [SwapToken[], number[]];
  swapTokenBalances: Record<string, number>;
  isLoadingSwapTokenInfo: boolean;
  isLoadingSwapTokenBalances: boolean;
  optionQuotes: Record<number, RebirthOptionQuote>;
  isLoadingQuotes: boolean;
  stableSwapTokenId: number | null;
}

const initialState: RebirthState = {
  isRebirthing: false,
  selectedOptionId: null,
  swapTokenInfo: [[], []],
  swapTokenBalances: {},
  isLoadingSwapTokenInfo: false,
  isLoadingSwapTokenBalances: false,
  optionQuotes: {},
  isLoadingQuotes: false,
  stableSwapTokenId: null,
};

export const getRebirthSwapTokenInfo = createAsyncThunk(
  "rebirth/getSwapTokenInfo",
  async () => {
    const data = await getSwapTokens();

    const swapTokenList = data[0].map((tokenInfo, index) => {
      return {
        name: tokenInfo.name,
        tokenAddress: tokenInfo.tokenAddress,
        isStable: tokenInfo.isStable,
        isEnabled: tokenInfo.isEnabled,
        price: Number(tokenInfo.price),
        decimal: Number(tokenInfo.decimal),
        tokenId: index,
      } as SwapToken;
    });

    const swapTokenPrice = data[1].map((price, index) => {
      return Number(
        formatUnits(price, swapTokenList[index].decimal)
      );
    });

    const stableSwapTokenId =
      swapTokenList.find((token) => token.isStable && token.isEnabled)
        ?.tokenId ?? null;

    return { swapTokenList, swapTokenPrice, stableSwapTokenId };
  }
);

export const getRebirthSwapTokenBalance = createAsyncThunk(
  "rebirth/getSwapTokenBalance",
  async ({ account }: { account: string }, { getState }) => {
    const state = (getState() as { rebirth: RebirthState }).rebirth;

    const balanceArray = await Promise.all(
      state.swapTokenInfo[0].map(async (token: SwapToken) => {
        const balance = await getTokenBalance(token.tokenAddress, account);
        return { [token.name]: balance };
      })
    );

    return balanceArray.reduce<Record<string, number>>((acc, entry) => {
      const key = Object.keys(entry)[0];
      acc[key] = Number(entry[key]);
      return acc;
    }, {});
  }
);

export const fetchRebirthQuotes = createAsyncThunk(
  "rebirth/fetchRebirthQuotes",
  async ({ account }: { account: string }, { getState }) => {
    const state = (getState() as { rebirth: RebirthState }).rebirth;
    const stableSwapTokenId = state.stableSwapTokenId;

    if (stableSwapTokenId === null) {
      throw new Error("No stable swap token configured");
    }

    const stableToken = state.swapTokenInfo[0][stableSwapTokenId];
    const tokenDecimals = stableToken?.decimal ?? 18;

    const quotes = await Promise.all(
      REBIRTH_OPTIONS.map(async (option) => {
        const [quote, optionState] = await Promise.all([
          quoteRebirth(account, option.id, stableSwapTokenId, tokenDecimals),
          getRebirthOption(option.id),
        ]);

        return {
          optionId: option.id,
          usdCost: quote.usdCost,
          enabled: optionState.enabled,
        };
      })
    );

    return quotes.reduce<Record<number, RebirthOptionQuote>>((acc, item) => {
      acc[item.optionId] = {
        usdCost: item.usdCost,
        enabled: item.enabled,
      };
      return acc;
    }, {});
  }
);

export const confirmRebirthAction = createAsyncThunk(
  "rebirth/confirmRebirth",
  async ({
    account,
    optionId,
    swapTokenId,
    tokenAddress,
    tokenAmount,
  }: {
    account: string;
    optionId: number;
    swapTokenId: number;
    tokenAddress: string;
    tokenAmount: number;
  }) => {
    if (!config.mafiaRebirthAddress) {
      throw new Error("Rebirth contract not configured");
    }

    const tx = await initiateRebirth(
      optionId,
      swapTokenId,
      tokenAddress,
      tokenAmount,
      account
    );
    return tx.transactionHash as string;
  }
);

const rebirthSlice = createSlice({
  name: "rebirth",
  initialState,
  reducers: {
    setSelectedRebirthOption: (
      state,
      { payload }: { payload: number | null }
    ) => {
      state.selectedOptionId = payload;
    },
    clearRebirthSelection: (state) => {
      state.selectedOptionId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRebirthSwapTokenInfo.pending, (state) => {
      state.isLoadingSwapTokenInfo = true;
    });
    builder.addCase(getRebirthSwapTokenInfo.fulfilled, (state, { payload }) => {
      state.isLoadingSwapTokenInfo = false;
      state.swapTokenInfo = [payload.swapTokenList, payload.swapTokenPrice];
      state.stableSwapTokenId = payload.stableSwapTokenId;
    });
    builder.addCase(getRebirthSwapTokenInfo.rejected, (state) => {
      state.isLoadingSwapTokenInfo = false;
    });

    builder.addCase(getRebirthSwapTokenBalance.pending, (state) => {
      state.isLoadingSwapTokenBalances = true;
    });
    builder.addCase(
      getRebirthSwapTokenBalance.fulfilled,
      (state, { payload }) => {
        state.isLoadingSwapTokenBalances = false;
        state.swapTokenBalances = payload;
      }
    );
    builder.addCase(getRebirthSwapTokenBalance.rejected, (state) => {
      state.isLoadingSwapTokenBalances = false;
    });

    builder.addCase(fetchRebirthQuotes.pending, (state) => {
      state.isLoadingQuotes = true;
    });
    builder.addCase(fetchRebirthQuotes.fulfilled, (state, { payload }) => {
      state.isLoadingQuotes = false;
      state.optionQuotes = payload;
    });
    builder.addCase(fetchRebirthQuotes.rejected, (state) => {
      state.isLoadingQuotes = false;
    });

    builder.addCase(confirmRebirthAction.pending, (state) => {
      state.isRebirthing = true;
    });
    builder.addCase(confirmRebirthAction.fulfilled, (state) => {
      state.isRebirthing = false;
      state.selectedOptionId = null;
    });
    builder.addCase(confirmRebirthAction.rejected, (state) => {
      state.isRebirthing = false;
    });
  },
});

export const { setSelectedRebirthOption, clearRebirthSelection } =
  rebirthSlice.actions;

export default rebirthSlice.reducer;
